import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  getOAuthCallbackDomain,
  hostname,
  isSafeUrl,
} from "../../../utils";

interface FacebookIntegrationFormProps {
  editMode: boolean;
  initialData?: {
    appId?: string;
    appSecret?: string;
    pageId?: string;
    existingParameters?: any; // All existing parameters to preserve on edit
  };
  companyId: string;
  instanceName: string;
  integrationId?: string; // Integration ID for edit mode
  getToken: () => Promise<string>;
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: any) => void;
  onOAuthComplete?: () => void;
  onStartOAuth?: (startOAuthFn: () => Promise<void>) => void;
}

export default function FacebookIntegrationForm({
  editMode,
  initialData = {},
  companyId,
  instanceName,
  integrationId,
  getToken,
  onValidationChange,
  onDataChange,
  onOAuthComplete,
  onStartOAuth,
}: FacebookIntegrationFormProps) {
  const [appId, setAppId] = useState(initialData.appId || "");
  const [appSecret, setAppSecret] = useState(initialData.appSecret || "");
  const [pageId, setPageId] = useState(initialData.pageId || "");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Get the OAuth redirect URL (supports whitelabel domains and uses ngrok for localhost)
  const getRedirectUrl = () => {
    return `${getOAuthCallbackDomain()}/integrations/meta/callback`;
  };

  useEffect(() => {
    // Validate form - all fields are required for OAuth
    const isValid = appId.trim() !== "" && appSecret.trim() !== "" && pageId.trim() !== "";
    onValidationChange(isValid);

    // Update parent with form data - preserve existing parameters in edit mode
    const formData = {
      appId: appId.trim(),
      appSecret: appSecret.trim(),
      pageId: pageId.trim(),
    };

    // In edit mode, preserve all existing parameters
    if (editMode && initialData.existingParameters) {
      onDataChange({
        ...formData,
        existingParameters: initialData.existingParameters,
      });
    } else {
      onDataChange(formData);
    }

    // Pass the OAuth start function to parent so Install button can trigger it
    if (onStartOAuth) {
      onStartOAuth(handleStartOAuth);
    }
  }, [appId, appSecret, pageId, editMode, initialData.existingParameters, onValidationChange, onDataChange]);

  const handleStartOAuth = async () => {
    if (!appId.trim() || !appSecret.trim() || !pageId.trim()) {
      setError("Please provide App ID, App Secret, and Facebook Page ID");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      console.log("Starting Meta OAuth...");
      console.log("Company ID:", companyId);
      console.log("Instance Name:", instanceName);
      console.log("Hostname:", hostname);

      const token = await getToken();
      console.log("Got auth token");

      const response = await axios.post(
        `${hostname}/integrations/meta/oauth/start`,
        {
          companyId,
          appId: appId.trim(),
          appSecret: appSecret.trim(),
          pageId: pageId.trim(),
          instanceName: instanceName,
          integrationId: integrationId, // Pass integration ID for edit mode
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("Response:", response.data);
      const { authUrl } = response.data;

      // Open Meta OAuth in a popup window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const safeUrlPattern = /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9.\-:/\\?&=%_+~#]+$/;
      if (!safeUrlPattern.test(authUrl)) return;
      const popup = window.open(
        authUrl,
        "MetaOAuth",
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
      );

      // Listen for success message from popup
      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === 'META_AUTH_SUCCESS') {
          console.log('Meta auth successful:', event.data);
          window.removeEventListener('message', handleMessage);

          // Call the completion callback if provided
          if (onOAuthComplete) {
            onOAuthComplete();
          }
        }
      };

      window.addEventListener('message', handleMessage);

      // Check if popup was blocked
      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        setError('Popup was blocked. Please allow popups for this site and try again.');
        setIsLoading(false);
      }
    } catch (err: any) {
      console.error("Error starting Meta OAuth:", err);
      console.error("Error details:", {
        message: err.message,
        response: err.response,
        stack: err.stack
      });

      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to start OAuth flow. Please check your App credentials."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" component="div">
          <strong>Connect your Meta (Facebook/Instagram) Pages using OAuth</strong>
          <br />
          <br />
          <strong>Step 1: Create a Meta App</strong>
          <ol style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
            <li>
              Go to{" "}
              <Link
                href="https://developers.facebook.com/apps"
                target="_blank"
                rel="noopener noreferrer"
              >
                Meta App Dashboard
              </Link>
            </li>
            <li>Click "Create App"</li>
            <li>Select "Business" as the app type</li>
            <li>Fill in app details and create</li>
            <li>Go to Settings → Basic</li>
            <li>Copy your "App ID" and "App Secret"</li>
            <li>
              <strong>IMPORTANT:</strong> Add OAuth redirect URI:
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  borderRadius: 1,
                  fontFamily: "monospace",
                }}
              >
                {getRedirectUrl()}
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                (Add this in Settings → Basic → App Domains, and in Facebook
                Login → Settings → Valid OAuth Redirect URIs)
              </Typography>
            </li>
          </ol>
          <br />
          <strong>
            Step 2: Enter your App credentials below and click "Install"
          </strong>
          <br />
          <Typography
            variant="caption"
            sx={{ mt: 1, display: "block", fontStyle: "italic" }}
          >
            ✅ Your access token will never expire (unless you change your password or revoke permissions)
            <br />
            ✅ Your first Page will be automatically connected
            <br />✅ Secure OAuth flow - no manual token copying required
          </Typography>
        </Typography>
      </Alert>

      <TextField
        fullWidth
        label="Meta App ID"
        value={appId}
        onChange={(e) => setAppId(e.target.value)}
        helperText="Your Meta App ID from developers.facebook.com"
        required
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Meta App Secret"
        value={appSecret}
        onChange={(e) => setAppSecret(e.target.value)}
        helperText="Your Meta App Secret (click 'Show' in App settings)"
        required
        type="password"
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Facebook Page ID"
        value={pageId}
        onChange={(e) => setPageId(e.target.value)}
        helperText="Your Facebook Page ID - required for Instagram posting"
        required
        sx={{ mb: 2 }}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2" component="div">
          <strong>How to find your Facebook Page ID:</strong>
          <ol style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
            <li>Log into Facebook, then click your profile photo in the top right</li>
            <li>Click "See all profiles", then select the Page you want to switch into</li>
            <li>Click your Page's name in the left menu to go to it</li>
            <li>Click the "About" tab below your Page's cover photo</li>
            <li>Click "Page transparency" to find your Page ID</li>
          </ol>
          <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
            <strong>Note:</strong> Your Instagram account must be linked to this Facebook Page to be able to post to Instagram.
          </Typography>
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Alert severity="warning">
        <Typography variant="caption">
          <strong>Note:</strong> After clicking "Install", you'll be redirected to Meta
          to authorize the app. Make sure pop-ups are not blocked.
        </Typography>
      </Alert>
    </Box>
  );
}
